import { Model, RoomModel, RootModel } from "./Model";
import { emitOrDefer, NO_MESSAGING, shouldBroadcast } from "./types";
import { ModelPath, ROOT_ID } from "./modelPath/ModelPath";
import { CreateSubModelMessage, DeleteModelMessage, MulticastDelegate2, MulticastDelegate3, MulticastDelegate4, PLAYERS_ROOM_UID, RemoveSubModelMessage, REPLAY_ROOM_UID, ROOT_MODEL_UID, UTILITY_ROOM_UID } from "flavr-common";
import { modelUIDRegistry } from "./modelUIDRegistry/ModelUIDRegistry";
import { modelNameToAbsolutePath } from "./modelPath/modelPathUtils";
import { determineModelNameForPrinting } from "./modelPoolUtils";
import { ModelFacetCloneContext } from "./modelUtils";
/**
 * The model pool manages the creation and destruction of models. It also allows to subscribe to model related events.
 */
class ModelPool {
    constructor() {
        // Model pool delegates
        this.onModelCreated = new MulticastDelegate2();
        this.onModelDestroyed = new MulticastDelegate3();
        // Model delegates
        this.onModelNameChanged = new MulticastDelegate3();
        this.onModelVisibilityChanged = new MulticastDelegate3();
        this.onModelTransformChanged = new MulticastDelegate3();
        this.onModelPositionChanged = new MulticastDelegate3();
        this.onModelOrientationChanged = new MulticastDelegate3();
        this.onModelScaleChanged = new MulticastDelegate3();
        this.onModelRoomChanged = new MulticastDelegate3();
        this.onModelParentChanged = new MulticastDelegate3();
        this.onModelChildAdded = new MulticastDelegate3();
        this.onModelChildRemoved = new MulticastDelegate3();
        this.onModelFacetChanged = new MulticastDelegate4();
        this.onModelFacetRemoved = new MulticastDelegate3();
        this.rooms = new Map();
        this.models = new Map();
        this.uidsInUse = new Set();
        // Connect private functions to be used by model module
        MODEL_POOL_ACCESS_FOR_MODEL_MODULE.setModelModule = this.setModelModule.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL_MODULE.setCurrentRoom = this.setCurrentRoom.bind(this);
        // Connect private functions to be used only by model
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelName = this.invokeModelName.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelVisibility = this.invokeModelVisibility.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelTransform = this.invokeModelTransform.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelPosition = this.invokeModelPosition.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelOrientation = this.invokeModelOrientation.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelScale = this.invokeModelScale.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelRoom = this.invokeModelRoom.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelParent = this.invokeModelParent.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelChildAdded = this.invokeModelChildAdded.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelChildRemoved = this.invokeModelChildRemoved.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelFacetChanged = this.invokeModelFacetChanged.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.invokeModelFacetRemoved = this.invokeModelFacetRemoved.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelNameChangedHandler = this.setOnModelNameChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelVisibilityChangedHandler = this.setOnModelVisibilityChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelTransformChangedHandler = this.setOnModelTransformChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelPositionChangedHandler = this.setOnModelPositionChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelOrientationChangedHandler = this.setOnModelOrientationChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelScaleChangedHandler = this.setOnModelScaleChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelRoomChangedHandler = this.setOnModelRoomChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelParentChangedHandler = this.setOnModelParentChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelChildAddedHandler = this.setOnModelChildAddedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelChildRemovedHandler = this.setOnModelChildRemovedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelFacetChangedHandler = this.setOnModelFacetChangedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.setOnModelFacetRemovedHandler = this.setOnModelFacetRemovedHandler.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.queueMessage = this.queueMessage.bind(this);
        MODEL_POOL_ACCESS_FOR_MODEL.flushMessages = this.flushMessages.bind(this);
        // Create root
        const root = this.createModelFull("root", Object.assign({ isRoot: true, uid: ROOT_MODEL_UID }, NO_MESSAGING));
        if (root === undefined)
            throw new Error("Couldn't create root model. This should not happen!");
        this.root = root;
        // ToDo: Ugly, utility room could be created elsewhere
        const utilityRoom = this.createModelFull("_utility", Object.assign({ parent: root, isRoom: true, uid: UTILITY_ROOM_UID }, NO_MESSAGING));
        if (utilityRoom === undefined)
            throw new Error("Couldn't create utility room model. This should not happen!");
        // ToDo: Check if this room can be removed !!!!!!!!!!!!!!!!!!!!!!!!!!!
        // ToDo: Ugly, replay room could be created elsewhere
        const replayRoom = this.createModelFull("replay", Object.assign({ parent: root, isRoom: true, uid: REPLAY_ROOM_UID }, NO_MESSAGING));
        if (replayRoom === undefined)
            throw new Error("Couldn't create replay room model. This should not happen!");
        const playersRoom = this.createModelFull("players", Object.assign({ parent: root, isRoom: true, uid: PLAYERS_ROOM_UID }, NO_MESSAGING));
        if (playersRoom === undefined)
            throw new Error("Couldn't create players room model. This should not happen!");
    }
    /// Model creation and destruction ///
    /**
     * Creates a new model based on the name and the parent. If no room was specified then the room will be determined
     * from the parent.
     *
     * @see {@link ModelCreationProps}
     * @see {@link BroadcastProps}
     */
    createModel(name, parent, props) {
        return this.createModelFull(name, Object.assign(Object.assign({}, props), { parent }));
    }
    /**
     * Creates a new room model based on the name and the parent. If no room was specified then the room will be determined
     * from the parent.
     *
     * @see {@link ModelCreationProps}
     * @see {@link BroadcastProps}
     *
     * The property `isStartRoom` shouldn't be used by the user. It is used in the session module to set the current
     * room during initialization.
     */
    createRoomModel(name, parent, props) {
        return this.createModelFull(name, Object.assign(Object.assign({}, props), { parent, isRoom: true }));
    }
    /**
     * Creates a new model based the provided properties. In most cases prefer using `createModel` instead. Use this
     * method only if you need full control over how the model parent and room are resolved
     *
    * @see {@link ModelCreationProps}
    * @see {@link BroadcastProps}
    */
    createModelFull(name, props) {
        var _a, _b, _c, _d;
        // Determine the room from the parent in case it was provided
        const parentForRoom = props === null || props === void 0 ? void 0 : props.parent;
        const roomFromParent = (parentForRoom !== undefined)
            ? (parentForRoom.isRoom()) ? parentForRoom : parentForRoom.getRoom()
            : undefined;
        let pathResolveProps = Object.assign(Object.assign({}, props), { name, parentUID: (_a = props === null || props === void 0 ? void 0 : props.parent) === null || _a === void 0 ? void 0 : _a.getUID(), parentPath: undefined, room: roomFromParent });
        const isRoom = (_c = (_b = props === null || props === void 0 ? void 0 : props.isRoom) !== null && _b !== void 0 ? _b : props === null || props === void 0 ? void 0 : props.isRoot) !== null && _c !== void 0 ? _c : false;
        const isRoot = (_d = props === null || props === void 0 ? void 0 : props.isRoot) !== null && _d !== void 0 ? _d : false;
        if (isRoot && (props === null || props === void 0 ? void 0 : props.fullPath) === undefined)
            pathResolveProps = Object.assign(Object.assign({}, pathResolveProps), { fullPath: ROOT_ID });
        const modelType = (isRoot) ? "root" : (isRoom) ? "room" : "model";
        // Create UID, because we need it to resolve the room and parent
        const uid = this.resolveUID(pathResolveProps);
        if (uid === undefined) {
            throw new Error(`Couldn't resolve UID for ${modelType} with name "${name}"`);
        }
        this.uidsInUse.add(uid);
        pathResolveProps = Object.assign(Object.assign({}, pathResolveProps), { uid });
        // Resolve parent and optionally room models based on the provided props
        const room = this.resolveRoom(pathResolveProps);
        const parent = this.resolveParent(pathResolveProps);
        if (parent === undefined && !isRoot) {
            throw new Error(`Couldn't create ${modelType} ${name} because no parent was provided`);
        }
        if (!(isRoom || isRoot) && room === undefined) {
            throw new Error(`Couldn't create ${modelType} ${name} because no room was provided`);
        }
        const parentPath = (parent !== undefined) ? modelUIDRegistry.getPathFromUID(parent.getUID()) : undefined;
        const modelPath = ModelPath.resolve(Object.assign(Object.assign({}, pathResolveProps), { parentPath }));
        if (modelPath === undefined)
            throw new Error(`Model path of ${name} couldn't be resolved`);
        // Create a model with a parent and also a room (unless the model itself is a room, in this case its optional)
        const model = (isRoot)
            ? new RootModel(uid)
            : ((isRoom)
                ? new RoomModel(uid, name)
                : new Model(uid, name));
        this.createModelCreationMessage(model, Object.assign(Object.assign({}, props), { path: modelPath.toString() }));
        if (parent !== undefined && !isRoot)
            model.setParent(parent, Object.assign(Object.assign({}, props), { broadcastTransform: false }));
        if (room !== undefined && !isRoot)
            model.setRoom(room, props);
        // Register model path
        modelUIDRegistry.addEntry(uid, modelPath);
        this.models.set(model.getUID(), model);
        if (isRoom)
            this.rooms.set(model.getUID(), model);
        // Set current room
        if ((props === null || props === void 0 ? void 0 : props.isStartRoom) === true)
            this.setCurrentRoom(model);
        emitOrDefer(props, (meta) => this.onModelCreated.invoke(model, meta));
        return model;
    }
    /**
     * Clones a model including all of its child models. If no facetWhitelist is specified, then all facets are cloned.
     * If a model with the same name as the clone already exists for the provided parent, then an error will be thrown.
     * However, if allowOverwritingModel is set to true then this model will be overwritten.
     *
     * @see {@link BroadcastProps}
     */
    cloneModel(model, props) {
        const clonedModel = this.cloneModelHierarchy(model, props);
        this.cloneModelHierarchyFacets(model, clonedModel, props);
        return clonedModel;
    }
    /**
     * Used to destroy this model and depending on how `handleChildren` was specified may also destroy the children.
     * If destroyChildrenOnly is set to true, then this model will not destroyed, but rather its children will be
     * destroyed. If destroyPermanently is set to true, then this model is permanently deleted from this session.
     * Lastly, if onlyGenerateMessages is set to true, then all messages are generated, but the models are not
     * actually destroyed.
     *
     * @see {@link BroadcastProps}
     */
    destroyModel(uidOrModel, props) {
        var _a, _b, _c, _d;
        const model = (uidOrModel instanceof Model) ? uidOrModel : this.models.get(uidOrModel);
        if (model === undefined)
            return;
        const uid = model.getUID();
        if (model.isRoot()) {
            console.warn(`[model] Destroying the root model is not allowed`);
            return;
        }
        const destroyChildrenOnly = (_a = props === null || props === void 0 ? void 0 : props.destroyChildrenOnly) !== null && _a !== void 0 ? _a : false;
        const handleChildren = (_b = props === null || props === void 0 ? void 0 : props.handleChildren) !== null && _b !== void 0 ? _b : "destroyRecursive";
        const destroyPermanently = (_c = props === null || props === void 0 ? void 0 : props.destroyPermanently) !== null && _c !== void 0 ? _c : false;
        // We are only interested in generating the messages but not actually destroying the model
        if ((props === null || props === void 0 ? void 0 : props.onlyGenerateMessages) === true) {
            switch (handleChildren) {
                case "destroyRecursive":
                    {
                        const children = [...model.getChildren()];
                        children.forEach(child => {
                            this.destroyModel(child.getUID(), Object.assign(Object.assign({}, props), { destroyChildrenOnly: false }));
                        });
                        break;
                    }
            }
            if (shouldBroadcast(props) && !destroyChildrenOnly) {
                if ((_d = props === null || props === void 0 ? void 0 : props.destroyPermanently) !== null && _d !== void 0 ? _d : false)
                    this.queueMessage(new DeleteModelMessage(uid), props);
                else
                    this.queueMessage(new RemoveSubModelMessage(uid), props);
            }
            return;
        }
        // First handle children
        switch (handleChildren) {
            case "destroyRecursive":
                {
                    const children = [...model.getChildren()];
                    children.forEach(child => {
                        this.destroyModel(child.getUID(), Object.assign(Object.assign({}, props), { destroyChildrenOnly: false }));
                    });
                    break;
                }
            case "attachToRoom":
                model.getRoomOrThrow().addChildren(model.getChildren(), props);
                break;
            case "attachToGrandparent":
                model.getParentOrThrow().getParentOrThrow().addChildren(model.getChildren(), props);
        }
        // We are only interested in removing the children
        if (destroyChildrenOnly)
            return;
        // We need to signal model destruction before actually removing the parent and room.
        // Note: if defer=true the event fires after detach/delete, so handlers can no longer find
        // the model via modelPool.getModel(). Avoid defer=true when destroying models.
        emitOrDefer(props, (meta) => this.onModelDestroyed.invoke(model, destroyPermanently, meta));
        // Remove itself from parent and room
        model._detachForDeletion();
        this.models.delete(uid);
        if (model.isRoom())
            this.rooms.delete(uid);
        this.uidsInUse.delete(uid);
        modelUIDRegistry.removeEntry(uid);
        if (shouldBroadcast(props)) {
            if (destroyPermanently)
                this.queueMessage(new DeleteModelMessage(uid), props);
            else
                this.queueMessage(new RemoveSubModelMessage(uid), props);
        }
    }
    /// Getter and setter ///
    getRoot() {
        return this.root;
    }
    hasModel(uid) {
        return this.models.has(uid);
    }
    getModel(uid) {
        return this.models.get(uid);
    }
    getModelOrThrow(uid) {
        if (!this.models.has(uid))
            throw new Error(`[model] Model "${determineModelNameForPrinting(uid)}" does not exist in the model pool`);
        return this.models.get(uid);
    }
    getRoom(uid) {
        return this.rooms.get(uid);
    }
    getRoomOrThrow(uid) {
        if (!this.rooms.has(uid))
            throw new Error(`[model] Model with uid "${uid}" does not exist in the model pool`);
        return this.rooms.get(uid);
    }
    getCurrentRoom() {
        return this.currentRoom;
    }
    /**
     * The primary room is the top level room of the layout. It is set once and never changes while staying in the same
     * layouter. If you want the room the user currently resides in use `getCurrentRoom()` instead
     */
    getPrimaryRoom() {
        return this.primaryRoom;
    }
    /// Find methods ///
    /**
     * Returns a list of all models that have the specified name. Ideally the name should be unique for each model, but
     * this is not guaranteed
     */
    findModelsWithName(nameOrPart, options) {
        var _a;
        // ToDo: Make more efficient by storing models in a Map<string, Model[]> or alternatively in a Map<string, Model> if
        //  we enforce that models have to have unique names
        const exactMatch = (_a = options === null || options === void 0 ? void 0 : options.exactMatch) !== null && _a !== void 0 ? _a : false;
        return Array.from(this.models.values())
            .filter(model => exactMatch ? model.getName() === nameOrPart : model.getName().includes(nameOrPart));
    }
    /**
     * Finds a model that is a descendant of `startModel` and has the specified name, or returns undefined if no such
     * model was found
     */
    findModelInHierarchy(name, startModel = this.root) {
        const searchStartModel = (startModel instanceof Model)
            ? startModel
            : this.getModel(startModel);
        if (searchStartModel === undefined)
            return undefined;
        if (searchStartModel.getName() === name)
            return searchStartModel;
        const modelQueue = [];
        modelQueue.push(searchStartModel);
        while (modelQueue.length > 0) {
            const currentModel = modelQueue.shift();
            if (currentModel === undefined)
                continue;
            if (currentModel.getName() === name)
                return currentModel;
            currentModel
                .getChildren()
                .forEach(child => modelQueue.push(child));
        }
        return undefined;
    }
    /**
     * Returns a list of all models that have all the specified facets
     */
    findModelsWithFacets(...facetTypes) {
        // ToDo: Can be improved by creating a map between facet type and model ids
        const models = Array.from(this.models.values());
        return models.filter(model => model.hasFacets(...facetTypes));
    }
    /// Messaging methods ///
    /**
     * Adds a message to the message queue. The order in which messages are created or added is preserved
     * across multiple models as well as the model pool. You can achieve the same by calling `model.addMessage(...)`.
     * The message is also handled locally by the ModelModule if it has the respective message handler. If you don't want
     * that the ModelModule handles the message set `props.handleLocally` to false
     */
    addMessage(message, props) {
        this.queueMessage(message, Object.assign({ handleMessageInModelModule: true }, props));
    }
    /**
     * Adds multiple messages to the message queue. The order in which messages are created or added is preserved
     * across multiple models as well as the model pool. You can achieve the same by calling `model.addMessages(...)`.
     * The message is also handled locally by the ModelModule if it has the respective message handler. If you don't want
     * that the ModelModule handles the message set `props.handleLocally` to false
     */
    addMessages(messages, props) {
        messages.forEach(message => {
            this.queueMessage(message, Object.assign({ handleMessageInModelModule: true }, props));
        });
    }
    /**
     * Sends all the messages that were created by any model as well as the model pool. You can also
     * call `model.flushMessages()` on any model to achieve the same effect.
     */
    flushMessages(props) {
        var _a;
        (_a = this.modelModule) === null || _a === void 0 ? void 0 : _a.flushMessages(props);
    }
    /// Register model room event handlers ///
    /**
     * Registers a handler that is called each time a model was created.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelCreatedHandler(handler) {
        this.onModelCreated.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelCreated);
    }
    /**
     * Registers a handler that is called each time a model was destroyed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelDestroyedHandler(handler) {
        this.onModelDestroyed.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelDestroyed);
    }
    /// Register model event handlers ///
    /**
     * Registers a handler that is called each time the name of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelNameChangedHandler(handler) {
        this.onModelNameChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelNameChanged);
    }
    /**
     * Registers a handler that is called each time the visibility of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelVisibilityChangedHandler(handler) {
        this.onModelVisibilityChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelVisibilityChanged);
    }
    /**
     * Registers a handler that is called each time the local transform of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelTransformChangedHandler(handler) {
        this.onModelTransformChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelTransformChanged);
    }
    /**
     * Registers a handler that is called each time the local position of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelPositionChangedHandler(handler) {
        this.onModelPositionChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelPositionChanged);
    }
    /**
     * Registers a handler that is called each time the local orientation of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelOrientationChangedHandler(handler) {
        this.onModelOrientationChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelOrientationChanged);
    }
    /**
     * Registers a handler that is called each time the local scale of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelScaleChangedHandler(handler) {
        this.onModelScaleChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelScaleChanged);
    }
    /**
     * Registers a handler that is called each time the room of a model has changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelRoomChangedHandler(handler) {
        this.onModelRoomChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelRoomChanged);
    }
    /**
     * Registers a handler that is called each time the parent of a model was changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelParentChangedHandler(handler) {
        this.onModelParentChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelParentChanged);
    }
    /**
     * Registers a handler that is called each time a child was added to a model.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelChildAddedHandler(handler) {
        this.onModelChildAdded.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelChildAdded);
    }
    /**
     * Registers a handler that is called each time a child was removed from a model.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelChildRemovedHandler(handler) {
        this.onModelChildRemoved.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelChildRemoved);
    }
    /**
     * Registers a handler that is called each time the facet of a model was added or changed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelFacetChangedHandler(handler) {
        this.onModelFacetChanged.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelFacetChanged);
    }
    /**
     * Registers a handler that is called each time a facet of a model was removed.
     *
     * If you want to react on a specific model use the same function in Model instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = modelPool.setOnModel***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnModelFacetRemovedHandler(handler) {
        this.onModelFacetRemoved.add(handler);
        return this.createUnsubscribeHandler(handler, this.onModelFacetRemoved);
    }
    /// Methods only used by ModelModule ///
    invokeModelName(model, name, meta) {
        this.onModelNameChanged.invoke(model, name, meta);
    }
    invokeModelVisibility(model, visible, meta) {
        this.onModelVisibilityChanged.invoke(model, visible, meta);
    }
    invokeModelTransform(model, transform, meta) {
        this.onModelTransformChanged.invoke(model, transform, meta);
    }
    invokeModelPosition(model, position, meta) {
        this.onModelPositionChanged.invoke(model, position, meta);
    }
    invokeModelOrientation(model, orientation, meta) {
        this.onModelOrientationChanged.invoke(model, orientation, meta);
    }
    invokeModelScale(model, scale, meta) {
        this.onModelScaleChanged.invoke(model, scale, meta);
    }
    invokeModelRoom(model, room, meta) {
        this.onModelRoomChanged.invoke(model, room, meta);
    }
    invokeModelParent(model, parent, meta) {
        this.onModelParentChanged.invoke(model, parent, meta);
    }
    invokeModelChildAdded(model, child, meta) {
        this.onModelChildAdded.invoke(model, child, meta);
    }
    invokeModelChildRemoved(model, child, meta) {
        this.onModelChildRemoved.invoke(model, child, meta);
    }
    invokeModelFacetChanged(model, facet, created, meta) {
        this.onModelFacetChanged.invoke(model, facet, created, meta);
    }
    invokeModelFacetRemoved(model, facet, meta) {
        this.onModelFacetRemoved.invoke(model, facet, meta);
    }
    setModelModule(module) {
        this.modelModule = module;
    }
    setCurrentRoom(room) {
        if (!room.isRoom())
            throw new Error("[model] Setting a non-room model as current room");
        this.currentRoom = room;
        if (this.primaryRoom === undefined && !room.isRoot() && room.getRoomDepth() === 0)
            this.primaryRoom = room;
    }
    /// Resolve UID ///
    resolveUID(props) {
        if (props === undefined)
            return undefined;
        if (props.uid !== undefined)
            return props.uid;
        // Get parent path if a parent UID is provided
        const parentPath = (props.parentUID !== undefined)
            ? modelUIDRegistry.getPathFromUID(props.parentUID)
            : undefined;
        // Resolve model path either if the fullPath is provided or by constructing the path by combining
        // the path of the parent with the name of the child
        const path = ModelPath.resolve(Object.assign(Object.assign({}, props), { parentPath }));
        if (path === undefined)
            return undefined;
        return modelUIDRegistry.createUID(path);
    }
    resolveParent(props) {
        // The root has no room
        if ((props === null || props === void 0 ? void 0 : props.isRoot) === true)
            return undefined;
        if ((props === null || props === void 0 ? void 0 : props.parent) !== undefined)
            return props.parent;
        if ((props === null || props === void 0 ? void 0 : props.uid) !== undefined) {
            const uid = props.uid;
            const parentUID = modelUIDRegistry.getParentUID(uid);
            if (parentUID !== undefined) {
                return this.models.get(parentUID);
            }
        }
        return undefined;
    }
    resolveRoom(props) {
        // The root has no room
        if ((props === null || props === void 0 ? void 0 : props.isRoot) === true)
            return undefined;
        if ((props === null || props === void 0 ? void 0 : props.room) !== undefined)
            return props.room;
        if ((props === null || props === void 0 ? void 0 : props.uid) !== undefined) {
            const uid = props.uid;
            const roomUID = modelUIDRegistry.getRoomUID(uid);
            if (roomUID !== undefined) {
                const room = this.models.get(roomUID);
                if (room === undefined)
                    return undefined;
                if (!room.isRoom()) {
                    console.error(`[model] Resolved model ${room.getName()} is not a room`);
                    return undefined;
                }
                return room;
            }
        }
        return undefined;
    }
    /// Messaging helper methods ///
    createModelCreationMessage(model, props) {
        // Optionally send a message
        if (shouldBroadcast(props)) {
            const msg = new CreateSubModelMessage(model.getUID());
            msg.path = props.path;
            this.queueMessage(msg, props);
        }
    }
    queueMessage(message, props) {
        var _a;
        (_a = this.modelModule) === null || _a === void 0 ? void 0 : _a.queueMessage(message, props);
    }
    /// Handler helper methods ///
    createUnsubscribeHandler(handler, origin) {
        return () => { origin.remove(handler); };
    }
    /// Clone helper methods ///
    cloneModelHierarchy(model, props) {
        var _a, _b, _c;
        if (model instanceof RootModel)
            throw new Error(`[model] Cannot clone the root model!`);
        if (model instanceof RoomModel)
            throw new Error(`[model] ${determineModelNameForPrinting(model)}: Cannot clone room models!`);
        props = props !== null && props !== void 0 ? props : {};
        const newParent = (_a = props.parent) !== null && _a !== void 0 ? _a : model.getParent();
        if (newParent === undefined)
            throw new Error("[model] Couldn't clone model, because the original model has no parent. This should never happen.");
        const newRoom = (newParent === null || newParent === void 0 ? void 0 : newParent.isRoom()) ? newParent : newParent.getRoom();
        if (newRoom === undefined)
            throw new Error("[model] Couldn't clone model, because the original model has no room. This should never happen.");
        // If no name was specified, then we take the name of the original model and add a "_Clone" such that the parent
        // is guaranteed to have no two models with the same name
        let newName = props.name;
        if (newName === undefined) {
            const siblingNames = (_b = newParent.getChildren().map(child => child.getName())) !== null && _b !== void 0 ? _b : [];
            let cloneIdx = 1;
            newName = `${model.getName()}_Clone${cloneIdx++}`;
            while (siblingNames.includes(newName)) {
                newName = `${model.getName()}_Clone${cloneIdx++}`;
            }
        }
        // Ensure that we don't create a clone of the model with the same name and parent
        const childWithSameName = newParent.getChild(newName);
        if (childWithSameName !== undefined) {
            const allowOverwritingModel = (_c = props.allowOverwritingModel) !== null && _c !== void 0 ? _c : false;
            if (allowOverwritingModel)
                modelPool.destroyModel(childWithSameName, { destroyPermanently: true });
            else
                throw new Error(`[model] Couldn't clone model, because parent "${newParent.getName()}" has already a model with name "${newName}"`);
        }
        const path = modelNameToAbsolutePath(newName, model.isRoom(), newParent);
        const cloneUID = this.resolveUID({ fullPath: path });
        if (cloneUID === undefined)
            throw new Error("Resolving UID failed");
        this.uidsInUse.add(cloneUID);
        const clonedModel = new Model(cloneUID, newName);
        this.createModelCreationMessage(clonedModel, Object.assign(Object.assign({}, props), { path }));
        clonedModel.setParent(newParent, props);
        clonedModel.setRoom(newRoom, props);
        clonedModel.setTransform(model.getTransform(), props);
        clonedModel.setVisible(model.isVisible(), props);
        // Register cloned model
        this.models.set(clonedModel.getUID(), clonedModel);
        if (clonedModel.isRoom())
            this.rooms.set(clonedModel.getUID(), clonedModel);
        // Notify that a new model was created
        emitOrDefer(props, (meta) => this.onModelCreated.invoke(model, meta));
        // Recursively clone children
        const childProps = Object.assign(Object.assign({}, props), { name: undefined, parent: clonedModel });
        for (const child of model.getChildren()) {
            this.cloneModelHierarchy(child, childProps);
        }
        return clonedModel;
    }
    cloneModelHierarchyFacets(model, clonedModel, props) {
        // Push hierarchies of the original and cloned model in a flat array, the list length and model order should be
        // identical for both hierarchies as the cloneModelHierarchy method is deterministic
        const modelHierarchy = [];
        const clonedModelHierarchy = [];
        model.traverse(m => { modelHierarchy.push(m); });
        clonedModel.traverse(m => { clonedModelHierarchy.push(m); });
        if (modelHierarchy.length !== clonedModelHierarchy.length)
            throw new Error(`Length of original ${model.getName()} and cloned model ${clonedModel.getName()} hierarchies differ in length`);
        // Create the clone facet context that contains the mapping between the original models and their cloned counterparts
        const cloneFacetContext = new ModelFacetCloneContext(model, clonedModel);
        const facetTypeWhitelist = ((props === null || props === void 0 ? void 0 : props.facetWhitelist) !== undefined)
            ? props.facetWhitelist.map(facetType => facetType.typestatic)
            : undefined;
        for (let i = 0; i < modelHierarchy.length; i++) {
            const currentModel = modelHierarchy[i];
            const currentClonedModel = clonedModelHierarchy[i];
            for (const facet of currentModel.getFacets()) {
                if (facetTypeWhitelist !== undefined && !facetTypeWhitelist.includes(facet.type))
                    continue;
                const clonedFacet = facet.clone(cloneFacetContext);
                currentClonedModel.setFacet(clonedFacet, props);
            }
        }
    }
}
// Only give ModelModule access to these private methods
const MODEL_POOL_ACCESS_FOR_MODEL_MODULE = { setModelModule: () => { }, setCurrentRoom: () => { } };
// Only give Model access to these private methods
const MODEL_POOL_ACCESS_FOR_MODEL = {
    invokeModelName: () => () => { },
    invokeModelVisibility: () => () => { },
    invokeModelTransform: () => () => { },
    invokeModelPosition: () => () => { },
    invokeModelOrientation: () => () => { },
    invokeModelScale: () => () => { },
    invokeModelRoom: () => () => { },
    invokeModelParent: () => () => { },
    invokeModelChildAdded: () => () => { },
    invokeModelChildRemoved: () => () => { },
    invokeModelFacetChanged: () => () => { },
    invokeModelFacetRemoved: () => () => { },
    setOnModelNameChangedHandler: () => () => { },
    setOnModelVisibilityChangedHandler: () => () => { },
    setOnModelTransformChangedHandler: () => () => { },
    setOnModelPositionChangedHandler: () => () => { },
    setOnModelOrientationChangedHandler: () => () => { },
    setOnModelScaleChangedHandler: () => () => { },
    setOnModelRoomChangedHandler: () => () => { },
    setOnModelParentChangedHandler: () => () => { },
    setOnModelChildAddedHandler: () => () => { },
    setOnModelChildRemovedHandler: () => () => { },
    setOnModelFacetChangedHandler: () => () => { },
    setOnModelFacetRemovedHandler: () => () => { },
    queueMessage: () => { },
    flushMessages: () => { }
};
/**
 * Global model pool, responsible for creating and destroying models.
 * @see ModelPool
 */
const modelPool = new ModelPool();
export { modelPool, MODEL_POOL_ACCESS_FOR_MODEL_MODULE, MODEL_POOL_ACCESS_FOR_MODEL };
